<template>
   <Head>
      <title>{{ $t("meta.account.title") }}</title>
      <meta name="description" :content="$t('meta.account.content')" />
   </Head>

   <PageLoader :isLoading="isLoading" :loadErrors="loadErrors" @transition-end="transitionCompleted">
      <main class="container py-20" data-cy="account-page">
         <div class="max-w-xl">
            <h1 class="sr-only">{{ $t("meta.account.title") }}</h1>
            <!-- SVG icons which are used on the account page, but not elsewhere on the site -->
            <!-- These may now be referenced with <use /> -->
            <SvgIcons />
            <div v-if="accountCms?.sections" class="grid gap-y-16" data-cy="account-page-sections">
               <AccountSection
                  v-for="(section, index) in accountCms.sections"
                  :key="index"
                  :section="section"
                  :index="index"
               />
            </div>
         </div>
      </main>
   </PageLoader>
</template>

<script setup lang="ts">
import { Head } from "@unhead/vue/components";
import { useI18n } from "@/vue-i18n/vue-i18n";
import VueScrollTo from "vue-scrollto";
import { useUserStore } from "@/stores/userStore";
import { useAccountStore } from "@/stores/accountStore";
import { useApiRequest } from "@/composables/useApiRequest";
import type { AccountPageCms } from "@/components/account/accountTypes";
import ContentAPI from "@/data/api-layer/contentApi";

// Declarations
// -----------------------------------------
const $route = useRoute();
const userStore = useUserStore();
const accountStore = useAccountStore();
const $t = useI18n().t;

const { isLoading, execManyRequests, loadErrors } = useApiRequest();

const accountCms = ref<AccountPageCms>(null);

// Lifecycle
// -----------------------------------------
onMounted(async () => {
   accountStore.$reset();

   const responses = await execManyRequests([
      () => userStore.setUser(),
      () => ContentAPI.getAccount(),
      () => accountStore.setUserStudiesAndDocuments($t("validation.error_generic_msg")),
   ]);
   if (loadErrors.value.length > 0) return;

   accountCms.value = responses[1].value.content;
});

// Methods
// -----------------------------------------
function scrollTo(target) {
   VueScrollTo.scrollTo(target, 600, {
      offset: -100,
      onDone: (element) => {
         element.setAttribute("tabindex", -1);
         element.setAttribute("style", "border: 0; -moz-outline-style: none; outline: none;");
         setTimeout(() => {
            element.focus();
         }, 0);
      },
   });
}

function transitionCompleted() {
   // if the transition completed but there were errors, don't try to find the element to scroll to
   if (loadErrors.value.length > 0) return;

   // if the query param 'scrollTo' is present, scroll to the element with that id
   const scrollTarget = $route.query.scrollTo?.toString();
   if (scrollTarget) {
      scrollTo(`#${scrollTarget}`);
   }
}
</script>
